// ════════════════════════════════════════════════════════════════════
// LYRAEA — AlbumPage.js
// ────────────────────────────────────────────────────────────────────
// A record sleeve. Big cover, credit, running order, one button that
// starts the thing from the top — because that is what an album is FOR,
// and it is the one place in this app where shuffle would be rude.
//
// The running order comes from each track's own `order` field, not from a
// list held on the album (see the note in lyraeaData.js). So this page
// cannot show a track that no longer exists, and cannot miss one that was
// just added — there is no second copy of the membership to go stale.
// ════════════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { albumTracks, fmtTime, fmtPlays, useExtras, extrasFor } from "./lyraeaData";
import { Cover, Equalizer } from "./Player";
import { ExtrasPanel } from "./Extras";

const C = {
  void: "var(--void)", obsidian: "var(--obsidian)", slab: "var(--slab)",
  slabHi: "var(--slab-hi)", edge: "var(--edge)", edgeHot: "var(--edge-hot)",
  gold: "var(--gold)", goldHi: "var(--gold-hi)", violet: "var(--violet)",
  bone: "var(--bone)", ash: "var(--ash)", smoke: "var(--smoke)", verd: "var(--verd)",
  display: "var(--display)", mono: "var(--mono)", sans: "var(--sans)",
};

const Kicker = ({ children, tint = C.smoke, size = 9 }) => (
  <div style={{
    fontFamily: C.mono, fontSize: size, letterSpacing: ".28em",
    textTransform: "uppercase", color: tint,
  }}>{children}</div>
);

export default function AlbumPage({
  album, tracks, current, playing, liked,
  onBack, onPlay, onPlayAll, onArtist, onLike,
}) {
  const list = useMemo(() => albumTracks(album?.id, tracks), [album, tracks]);
  const allExtras = useExtras();
  const albumExtras = useMemo(
    () => (album?.id ? extrasFor(allExtras, { albumId: album.id }) : []),
    [allExtras, album]
  );
  const playable = useMemo(() => list.filter((t) => !t.priceDC), [list]);

  const totalSec = useMemo(
    () => list.reduce((n, t) => n + Number(t.durationSec || 0), 0),
    [list]
  );
  const totalPlays = useMemo(
    () => list.reduce((n, t) => n + Number(t.plays || 0), 0),
    [list]
  );

  if (!album) {
    return (
      <div style={{ padding: "40px 8px", textAlign: "center" }}>
        <div style={{ fontFamily: C.display, fontSize: 21, color: C.bone }}>That album is gone.</div>
        <button onClick={onBack} style={{
          background: "none", border: `1px solid ${C.edge}`, color: C.smoke, marginTop: 14,
          fontFamily: C.mono, fontSize: 9, letterSpacing: ".2em", padding: "8px 12px",
        }}>BACK</button>
      </div>
    );
  }

  const year = album.releasedAt ? new Date(album.releasedAt).getFullYear() : null;

  return (
    <div style={{ animation: "lyFade .25s both" }}>
      <button onClick={onBack} style={{
        background: "none", border: "none", color: C.ash, padding: "2px 0 12px",
        fontFamily: C.mono, fontSize: 10, letterSpacing: ".26em",
      }}>← BACK</button>

      {/* ── THE SLEEVE ────────────────────────────────────────── */}
      <div style={{
        display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap", alignItems: "flex-end",
      }}>
        <div style={{ position: "relative" }}>
          <Cover src={album.cover || list[0]?.cover} seed={album.id} title={album.title} size={168} glow />
          {/* the spine of light down the left edge, like a sleeve on a shelf */}
          <div style={{
            position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
            background: `linear-gradient(180deg, transparent, ${C.gold}88, transparent)`,
          }} />
        </div>

        <div style={{ flex: 1, minWidth: 220 }}>
          <Kicker tint={C.gold}>ALBUM</Kicker>
          <div style={{
            fontFamily: C.display, fontWeight: 800, fontSize: 32, color: C.bone,
            lineHeight: 1.05, margin: "4px 0 2px",
          }}>{album.title}</div>

          <div
            onClick={() => album.artistUid && onArtist?.(album.artistUid)}
            style={{
              fontFamily: C.display, fontStyle: "italic", fontSize: 16, color: C.gold,
              cursor: album.artistUid ? "pointer" : "default", display: "inline-block",
            }}
          >{album.artist || "Unknown"}</div>

          <div style={{
            fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".16em",
            color: C.smoke, marginTop: 8,
          }}>
            {[year, `${list.length} TRACK${list.length === 1 ? "" : "S"}`,
              totalSec ? fmtTime(totalSec) : null,
              totalPlays ? `${fmtPlays(totalPlays)} PLAYS` : null]
              .filter(Boolean).join("  ·  ")}
          </div>

          <button
            onClick={() => playable.length && onPlayAll(playable)}
            disabled={!playable.length}
            style={{
              marginTop: 13, border: "none", color: C.void,
              background: `linear-gradient(150deg, ${C.goldHi}, ${C.gold})`,
              fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".22em",
              padding: "11px 18px", opacity: playable.length ? 1 : .4,
            }}>▶ PLAY FROM THE TOP</button>
        </div>
      </div>

      {/* ── THE RUNNING ORDER ─────────────────────────────────── */}
      {list.length === 0 && (
        <div style={{ fontFamily: C.sans, fontSize: 14, color: C.smoke, padding: "18px 2px" }}>
          No tracks on this album yet. Put them on it from the Studio.
        </div>
      )}

      {list.map((t, i) => {
        const isCurrent = current === t.id;
        return (
          <div key={t.id}
            onClick={() => !t.priceDC && onPlay(t, playable)}
            style={{
              display: "flex", alignItems: "center", gap: 12, padding: "10px 6px",
              borderBottom: `1px solid ${C.edge}`,
              cursor: t.priceDC ? "default" : "pointer",
              opacity: t.priceDC ? .55 : 1,
              background: isCurrent ? `${C.violet}12` : "transparent",
            }}>
            <div style={{ width: 22, display: "grid", placeItems: "center" }}>
              {isCurrent
                ? <Equalizer color={playing ? C.verd : C.smoke} size={11} />
                : <span style={{ fontFamily: C.mono, fontSize: 11, color: C.smoke }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: C.sans, fontSize: 16, color: isCurrent ? C.goldHi : C.bone,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{t.title}</div>
              {t.plays ? (
                <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: ".12em", color: C.smoke }}>
                  {fmtPlays(t.plays)} PLAYS
                </div>
              ) : null}
            </div>

            <span style={{
              fontFamily: C.mono, fontSize: 10,
              color: t.priceDC ? C.gold : C.smoke, whiteSpace: "nowrap",
            }}>
              {t.priceDC ? `◆ ${t.priceDC}` : fmtTime(t.durationSec)}
            </span>

            {onLike && (
              <button
                onClick={(e) => { e.stopPropagation(); onLike(t); }}
                style={{
                  background: "none", border: "none", padding: "4px 2px",
                  color: liked?.(t.id) ? C.gold : C.edgeHot,
                  fontSize: 15, fontFamily: C.display,
                }}
              >{liked?.(t.id) ? "◆" : "◇"}</button>
            )}
          </div>
        );
      })}

      {/* ── WHAT CAME WITH THE RECORD ────────────────────────────────
          Album-level extras only. A track's own lyrics belong to the
          track and live in the now-playing sheet; putting them here too
          would mean the same words in two places, which is the shape
          that eventually disagrees with itself. Liner notes are the
          thing that genuinely belongs to the whole record, and this is
          where a sleeve has always kept them. */}
      {albumExtras.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <Kicker tint={C.violet}>WITH THE RECORD</Kicker>
          <div style={{ height: 8 }} />
          <ExtrasPanel extras={albumExtras} />
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE SHELF — a scrollable rail of sleeves, used on Library and Artist
// ════════════════════════════════════════════════════════════════════
export function AlbumRail({ albums, tracks, onOpen, label = "ALBUMS", size = 118, bare }) {
  if (!albums?.length) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      {!bare && <Kicker tint={C.gold}>{label}</Kicker>}
      <div className="ly-scroll" style={{
        display: "flex", gap: 12, overflowX: "auto", padding: "10px 0 6px",
      }}>
        {albums.map((al) => {
          const n = tracks.filter((t) => t.albumId === al.id).length;
          const cover = al.cover || tracks.find((t) => t.albumId === al.id)?.cover;
          return (
            <div key={al.id} className="ly-sleeve" onClick={() => onOpen(al.id)}
              style={{ width: size, flex: "0 0 auto" }}>
              <Cover src={cover} seed={al.id} title={al.title} size={size} />
              <div className="ly-sleeve-title" style={{
                fontFamily: C.sans, fontSize: 13.5, color: C.bone, marginTop: 6,
                transition: "color .3s",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{al.title}</div>
              <div style={{
                fontFamily: C.mono, fontSize: 8.5, letterSpacing: ".12em", color: C.smoke,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{n} TRACK{n === 1 ? "" : "S"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
